// apps/merchant-v3/src/components/BEPrereqsBanner.tsx
//
// Phase 6 Wave 2 Task 04 — Yellow notice banner shown when one or more
// cross-phase BE prerequisites (P1/P3/P5 patches) are not
// yet deployed. Probed at app boot by `checkBEPrereqs()`; the boolean
// `usePrereqStore.bePrereqsIncomplete` drives this component.
//
// Behaviour:
//   - Renders only when `bePrereqsIncomplete === true`.
//   - Dismissible — sessionStorage flag `pw_prereq_banner_dismissed`
//     hides for the rest of the tab session. New tab / reload re-shows.
//   - Text VI per task body: "Một số tính năng tạm thời bị tắt do BE
//     prereqs chưa sẵn sàng. Liên hệ team API để check status."
//   - role="status" (not "alert") because this is informational and
//     non-urgent. aria-live="polite" so screen readers don't interrupt.
//
// vg-binding: PLAN:task-04
// vg-binding: P6.D-235
// vg-binding: P6.D-229
// vg-binding: P6.D-230
// vg-binding: P6.D-232
// vg-binding: P6.D-237

import { useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useBEPrereqsIncomplete } from '@/store/prereq';

/** Session-scoped dismissal — re-shows on next tab/reload so merchants
 *  aren't left in the dark if the patches still haven't shipped. */
const DISMISS_KEY = 'pw_prereq_banner_dismissed';

function isDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function persistDismiss(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* sessionStorage disabled — silent no-op, banner just re-renders */
  }
}

export function BEPrereqsBanner(): JSX.Element | null {
  const { t } = useTranslation('common');
  const incomplete = useBEPrereqsIncomplete();
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed());

  // If `incomplete` flips back to false (BE patches shipped), clear the
  // session flag so the banner can re-appear if a future probe fails.
  useEffect(() => {
    if (!incomplete && dismissed) {
      try {
        window.sessionStorage.removeItem(DISMISS_KEY);
      } catch {
        /* no-op */
      }
      setDismissed(false);
    }
  }, [incomplete, dismissed]);

  if (!incomplete || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="be-prereqs-banner"
      className="flex items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
    >
      <Info className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
      <p className="flex-1">
        Một số tính năng tạm thời bị tắt do BE prereqs chưa sẵn sàng.
        Liên hệ team API để check status.
      </p>
      <button
        type="button"
        onClick={() => {
          persistDismiss();
          setDismissed(true);
        }}
        aria-label={t('a11y.dismissNotification')}
        className="rounded p-1 text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export default BEPrereqsBanner;
